<template>
  <v-card flat color="transparent">
    <v-card-text v-if="isRecalculating" class="d-flex flex-nowrap align-end text-pwt_medium_grey pl-5 py-0">
      recalculating <PulseLoader style="padding-bottom: 4px;" />
    </v-card-text>
    <v-card-text class="bg-transparent px-3" :class="comparison ? 'pl-1' : 'pr-1'">
      <!-- Conditionally rendered trip stats section if a trip is selected. -->
      <v-card flat v-if="tripStats && trip" color="transparent" class="px-1">
        <!-- vehicle section -->
        <v-card
          class="pa-2 ma-1 mb-3 d-flex flex-column rounded-lg"
          elevation="3"
          color="transparent"
          :min-height="large ? '120px' : undefined"
        >
          <v-row no-gutters class="align-center flex-nowrap">
            <div
              style="height: 70px; width: 70px"
              class="flex-grow-0 flex-shrink-0 d-flex align-center justify-center"
            >
              <v-img
                :lazy-src="getSrc('car_images/placeholder_car.png')"
                :src="getImageSrc()"
                contain
              />
            </div>
            <div class="flex-grow-1 flex-shrink-1 overflow-hidden">
              <v-card-title class="py-0 text-truncate">
                {{ vehicle?.licensePlate ? vehicle.licensePlate : 'no plate' }}
              </v-card-title>
              <v-card-subtitle class="pb-0 text-truncate">
                {{ vehicle?.evModel ? vehicle.evModel.name : 'model not specified' }}
              </v-card-subtitle>
            </div>
            <v-btn
              icon
              class="flex-shrink-0 flex-grow-0"
              size="small"
              flat
              @click="() => (showVehicleGalleryDialog = true)"
            >
              <v-icon> mdi-pencil </v-icon>
            </v-btn>
          </v-row>
        </v-card>
        <!-- Performance -->
        <v-card class="rounded-lg ma-1 mb-3" color="transparent">
          <v-card-title style="font-size: 16px">Performance summary</v-card-title>
          <v-card-text>
            <v-row no-gutters class="mb-3">
              <v-icon color="primary" class="mr-1"> mdi-clock-outline </v-icon>
              {{ drivingTime }}
              driving time
            </v-row>
            <v-row no-gutters>
              <v-icon color="primary" class="mr-1"> mdi-map-marker-radius-outline </v-icon>
              {{ drivingDistance }}
              km distance travelled
            </v-row>
          </v-card-text>
          <v-card-title class="text-body-1 font-weight-medium">
            {{ displayAsHydrogen ? 'Fuelling time' : 'Charging time' }}
          </v-card-title>
          <v-card-text>
            <v-card
              v-if="hasInvalidEnergyConsumption"
              class="bg-background pa-2 flex-grow-1 rounded-lg"
              flat
            >
              <span>
                <span class="text-primary mr-1 font-weight-bold">!</span>
                <span class="font-weight-regular">
                  We are unable to calculate the charging time for this trip
                </span>
              </span>
            </v-card>
            <v-card
              v-if="
                (!hasInvalidEnergyConsumption && chargingTimeToUse === 'DC') ||
                (!hasInvalidEnergyConsumption && chargingTimeToUse === 'AC DC split')
              "
              class="bg-background pa-2 flex-grow-1 rounded-lg"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">DC</span>
                <span class="font-weight-regular">
                  {{
                    chargingTimeToUse === 'AC DC split'
                      ? (acDcSplit?.dcChargingTime ?? 0)
                      : dcChargingTime
                  }}
                </span>
              </v-row>
            </v-card>
            <v-card
              v-if="
                (!hasInvalidEnergyConsumption && chargingTimeToUse === 'AC') ||
                (!hasInvalidEnergyConsumption && chargingTimeToUse === 'AC DC split')
              "
              class="bg-background pa-2 flex-grow-1 rounded-lg mb-2"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">AC</span>
                <span class="font-weight-regular">
                  {{
                    chargingTimeToUse === 'AC DC split'
                      ? (acDcSplit?.acChargingTime ?? 0)
                      : acChargingTime
                  }}
                </span>
              </v-row>
            </v-card>
            <v-card
              v-if="!hasInvalidEnergyConsumption && chargingTimeToUse === 'H'"
              class="bg-background pa-2 flex-grow-1 rounded-lg"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">H₂</span>
                <span class="font-weight-regular">
                  {{ HydrogenChargingTime }}
                </span>
              </v-row>
            </v-card>
          </v-card-text>
          <v-card-title class="text-body-1 font-weight-medium">
            {{ displayAsHydrogen ? 'Fuel use' : 'Battery usage' }}
          </v-card-title>
          <v-card-text>
            <v-row no-gutters class="mb-3">
              <v-card class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary">
                  {{ displayAsHydrogen ? 'mdi-hydrogen-station' : 'mdi-ev-station' }}
                </v-icon>
                {{ numberOfChargingStopsNeeded }}
              </v-card>
            </v-row>
            <v-row no-gutters class="mb-3">
              <v-card class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary">
                  {{ displayAsHydrogen ? 'mdi-meter-gas-outline' : 'mdi-battery-plus' }}
                </v-icon>
                {{ totalChargeUsed }}
              </v-card>
            </v-row>
            <v-row no-gutters>
              <v-card
                v-if="displayAsHydrogen"
                class="bg-background pa-2 rounded-lg flex-grow-1 text-caption"
                flat
              >
                <v-icon color="primary"> custom:hydrogen </v-icon>
                {{ totalHydrogen }}
                kg needed to complete this trip
              </v-card>
              <v-card v-else class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary"> custom:offlineBolt </v-icon>
                {{ totalEnergy }}
                kWh needed to complete this trip
              </v-card>
            </v-row>
          </v-card-text>
        </v-card>
        <!-- Savings -->
        <v-card class="rounded-lg mb-3" color="transparent">
          <v-card-title style="font-size: 16px; min-height: 56px" class="d-flex align-center">
            <v-icon color="primary" class="mr-1"> mdi-piggy-bank-outline </v-icon>
            ${{ fuelSavings }}
            fuel savings
            <StyledToolTip v-if="comparison && fuelSavingsTooltip" :data="fuelSavingsTooltip" />
          </v-card-title>
        </v-card>
        <!-- emissions savings -->
        <v-card class="rounded-lg mb-3" color="transparent">
          <v-card-title style="font-size: 14px; min-height: 56px" class="d-flex align-center">
            <v-icon color="primary" class="mr-1 flex-shrink-0" size="28"> custom:fireIcon </v-icon>
            {{ avoidedCO2 }}
            <div class="text-truncate flex-grow-1 flex-shrink-1">kg CO₂ emissions avoided</div>
            <StyledToolTip
              v-if="comparison && emissionsTooltip"
              :data="emissionsTooltip"
              class="flex-shrink-0"
            />
          </v-card-title>
        </v-card>
      </v-card>
      <!-- Conditionally rendered default section if no selected trip. -->
      <v-card flat v-else>
        <v-card-text> could not load trip stats </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
  <GenericDialog :activator="showVehicleGalleryDialog" @close="showVehicleGalleryDialog = false">
    <VehicleSearchContent @vehicle-select="handleVehicleSelectPreCheck" show-generic />
  </GenericDialog>
  <PotentialBrokenTripAlert
    :activator="showBrokenTripAlert"
    @close="handleVehicleSelectCleanup"
    @continue="handleContinueVehicleSelect"
  />
</template>

<script setup lang="ts">
import type Trip from '@/classes/trip_classes/trip'
import Vehicle from '@/classes/vehicle_classes/vehicle'
import PotentialBrokenTripAlert from '@/components/alert-popup/PotentialBrokenTripAlert.vue'
import StyledToolTip from '@/components/ui-elements/displays/StyledToolTip.vue'
import PulseLoader from '@/components/ui-elements/loaders/PulseLoader.vue'
import { CalcVsType, GettersTypes, MutationTypes, type State } from '@/store/store_types'
import type { TripStats } from '@/types/trip_specific_types'
import calcTripAcDcSplit from '@/utils/calcTripAcDcSplit'
import getAssetSrc from '@/utils/getAssetSrc'
import getDirectusImgUrl from '@/utils/getDirectusImgUrl'
import { isHydrogen, kWhToHydrogenKgs } from '@/utils/hydrogenUtils'
import parseIntOrFloat from '@/utils/parseIntOrFloat'
import { getNiceDuration } from '@/utils/timeUtils'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import GenericDialog from '../../dialog-wrappers/GenericDialog.vue'
import VehicleSearchContent from '../../trips/comparison/VehicleSearchContent.vue'

const { comparison = false, large = false } = defineProps<{
  comparison?: boolean
  large?: boolean
}>()

const store = useStore<State>()
const showVehicleGalleryDialog = ref(false)
const tempVehicle = ref<Vehicle | null>(null)
const showBrokenTripAlert = ref(false)
function handleVehicleSelectPreCheck(vehicle: Vehicle) {
  if (comparison) {
    // skip if is comparison as it would not break the trip
    handleVehicleSelect(vehicle)
  } else {
    tempVehicle.value = vehicle
    showBrokenTripAlert.value = true
  }
}
function handleContinueVehicleSelect() {
  if (tempVehicle.value) {
    handleVehicleSelect(tempVehicle.value as Vehicle)
  }
  handleVehicleSelectCleanup()
}
function handleVehicleSelectCleanup() {
  showBrokenTripAlert.value = false
  tempVehicle.value = null
  showVehicleGalleryDialog.value = false
}

const isRecalculating = ref(false)
async function handleVehicleSelect(vehicle: Vehicle) {
  // close dialog
  showVehicleGalleryDialog.value = false
  // update trip vehicle
  if (!trip.value) return
  // trigger UI indication
  isRecalculating.value = true
  if (comparison) {
    // handel comparison
    await trip.value.addComparison({ vehicle })
  } else {
    // handle trip
    trip.value.vehicle = vehicle
    if (trip.value.itinerary.tripDataType === 'evnav' && trip.value.canUseEVNav) {
      await trip.value.planTrip({
        recalculation: true,
        costPerKWhAC: store.state.defaultPublicCostPerKWh,
        costPerKWhDC: store.state.defaultPublicCostPerKWh,
        costPerMin: store.state.defaultCostPerMinDC,
        costPerKgH2: store.state.hydrogen_cost_per_kg,
      })
    } else {
      await trip.value.planLogisticsTrip(true)
    }
  }

  // update global state
  store.commit(MutationTypes.updateIndividualTrip, trip.value)
  // clear UI indication
  isRecalculating.value = false
}

const tripStats = computed<TripStats | undefined>(
  () => store.getters[GettersTypes.selectedTripStats],
)

const trip = computed<Trip | undefined>(() => store.getters[GettersTypes.selectedTripData])

const displayedComparisonData = computed(() => {
  return comparison ? trip.value?.displayedComparisonData : trip.value?.currentVehiclesComparison
})

const vehicle = computed<Vehicle | undefined>(() => {
  return comparison ? displayedComparisonData.value?.vehicle : trip.value?.vehicle
})

const displayAsHydrogen = computed(() => vehicle.value && isHydrogen(vehicle.value))

const hasStoredTrips = computed(() => store.state.trips.length > 0)
const chargers = computed(() => store.state.chargers)

const acDcSplit = computed(() => {
  if (!trip.value) return undefined
  if (comparison) return displayedComparisonData.value?.acDCSplit
  return calcTripAcDcSplit(trip.value, chargers.value)
})

const niceDuration = (seconds: number): string => getNiceDuration(seconds, 'short')

const compileNumChargesString = (battery: string | undefined): string => {
  if (!battery) return 'We could not calculate the number of charging stops needed'
  const parsedBattery = parseIntOrFloat(battery)
  if (!parsedBattery) return 'We could not calculate the number of charging stops needed'
  if (isNaN(parsedBattery)) return 'We could not calculate the number of charging stops needed'
  const numberOfChargesNeeded = parsedBattery / 80
  if (numberOfChargesNeeded % 1 === 0) {
    return (
      numberOfChargesNeeded +
      ' charging stop' +
      (numberOfChargesNeeded > 1 ? 's' : '') +
      ' required'
    )
  } else {
    return (
      Math.floor(numberOfChargesNeeded) +
      ' - ' +
      Math.ceil(numberOfChargesNeeded) +
      ' charging stops required'
    )
  }
}

function getImageSrc() {
  if (vehicle.value?.imageSrcPaths.length) return vehicle.value.imageSrcPaths[0]
  if (vehicle.value?.evModel?.imageUUID) return getDirectusImgUrl(vehicle.value.evModel.imageUUID)
  return getSrc('car_images/placeholder_car.png')
}

function getSrc(partialFilePath: string): string {
  return getAssetSrc(partialFilePath)
}

// computed stats

const drivingTime = computed(() => {
  if (displayedComparisonData.value) return niceDuration(displayedComparisonData.value.drivingTime)
  if (tripStats.value) return tripStats.value.drivingTime
  return 'unknown'
})

const drivingDistance = computed(() => {
  let meters = 0
  if (displayedComparisonData.value) meters = displayedComparisonData.value.drivingDistance
  else if (trip.value) meters = trip.value.itinerary.totalDrivingDistance
  return Math.round(meters / 1000)
})

const hasInvalidEnergyConsumption = computed(() => {
  if (!displayedComparisonData.value) return true
  if (!displayedComparisonData.value.totalEnergy) return true
  if (displayedComparisonData.value.totalEnergy <= 0) return true
  return false
})

const acChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData.value)
    chargingTime = displayedComparisonData.value.estimatedChargingTime.ac_charging_time
  else if (acDcSplit.value) chargingTime = acDcSplit.value.acChargingTime
  return niceDuration(chargingTime)
})

const dcChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData.value)
    chargingTime = displayedComparisonData.value.estimatedChargingTime.dc_charging_time
  else if (acDcSplit.value) chargingTime = acDcSplit.value.dcChargingTime
  return niceDuration(chargingTime)
})

const HydrogenChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData.value)
    chargingTime = displayedComparisonData.value.estimatedChargingTime.dc_charging_time
  else if (acDcSplit.value) chargingTime = acDcSplit.value.dcChargingTime
  return niceDuration(chargingTime / 33.3)
})

const numberOfChargingStopsNeeded = computed(() => {
  if (displayedComparisonData.value) {
    const numberOfChargesNeeded = displayedComparisonData.value.numberOfChargingStopsNeeded
    const lower = Math.floor(numberOfChargesNeeded)
    const higher = Math.ceil(numberOfChargesNeeded)

    if (lower === higher)
      return lower + (displayAsHydrogen.value ? ' fuel stops required' : ' charging stops required')
    return (
      lower +
      ' - ' +
      higher +
      (displayAsHydrogen.value ? ' fuel stops required' : ' charging stops required')
    )
  }
  if (tripStats.value) return compileNumChargesString(tripStats.value.battery)
  return 'We could not calculate the number of charging stops needed'
})

const totalChargeUsed = computed(() => {
  let percentage = 0
  if (displayedComparisonData.value)
    percentage = displayedComparisonData.value.totalChargeUsed * 100
  else if (tripStats.value?.battery) percentage = parseIntOrFloat(tripStats.value.battery) || 0
  // sanity check percentage
  if (percentage <= 0) {
    return (
      'we could not calculate the ' + (displayAsHydrogen.value ? 'hydrogen' : 'energy') + ' used'
    )
  }
  // compile proper string
  return (
    (percentage < 1 ? 'less than 1' : Math.round(percentage)) +
    (displayAsHydrogen.value ? '% of tank capacity used' : '% of battery capacity used')
  )
})

const totalEnergy = computed(() => {
  if (displayedComparisonData.value) return Math.round(displayedComparisonData.value.totalEnergy)
  if (tripStats.value?.totalEnergyUsed) return Math.round(tripStats.value.totalEnergyUsed)
  return 'we could not calculate the total'
})
const totalHydrogen = computed(() => {
  const totalEnergy = displayedComparisonData.value?.totalEnergy
  if (typeof totalEnergy === 'string') return totalEnergy
  if (typeof totalEnergy === 'number' && totalEnergy > 0) return 'less than 1'
  if (typeof totalEnergy === 'number') return Math.round(kWhToHydrogenKgs(totalEnergy))
  return 'we could not calculate the total'
})

const calcVs = computed(() => store.state.calcVs)
const petrolKmPerL = computed(() => store.state.petrolKmPerLitre)
const dieselKmPerL = computed(() => store.state.dieselKmPerLitre)
const fuelCostPerL = computed(() => {
  if (calcVs.value === CalcVsType.DIESEL) return store.state.dieselCostPerLitre
  return store.state.petrolCostPerLitre
})
const kmPerL = computed(() => {
  if (calcVs.value === CalcVsType.DIESEL) return dieselKmPerL.value
  return petrolKmPerL.value
})
const publicCostPerKW = computed(() => store.state.defaultPublicCostPerKWh)
const publicCostPerMin = computed(() => store.state.defaultCostPerMinDC)
const privateCostPerKW = computed(() => store.state.defaultHomeCostPerKWh)

const fuelSavings = computed(() => {
  if (displayedComparisonData.value)
    return (
      displayedComparisonData.value.equivalentFuelCost({
        fuelCostPerLitre: fuelCostPerL.value,
        kmPerLitre: kmPerL.value,
      }) -
      (displayAsHydrogen.value
        ? kWhToHydrogenKgs(displayedComparisonData.value.totalEnergy) *
          store.state.hydrogen_cost_per_kg
        : Math.max(
            displayedComparisonData.value.fastChargingCost({
              publicChargingCost: publicCostPerKW.value,
              publicChargingCostPerMin: publicCostPerMin.value,
            }),
            displayedComparisonData.value.homeChargingCost(privateCostPerKW.value),
          ))
    ).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  if (tripStats.value)
    return (
      tripStats.value.fuelCost -
      (tripStats.value.publicChargingCost || 0) -
      (tripStats.value.privateChargingCost || 0)
    ).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
})

const avoidedCO2 = computed(() => {
  if (displayedComparisonData.value)
    return Math.round(
      displayedComparisonData.value.calcAvoidedCO2({
        calcVs: calcVs.value,
        petrolKmPerLitre: petrolKmPerL.value,
        dieselKmPerLitre: dieselKmPerL.value,
        hydrogenCO2PerKg: store.state.hydrogen_CO2_kg_per_kg,
        electricityCO2PerKWh: store.state.electric_CO2_kg_per_kWh,
        calcAsHydrogen: displayAsHydrogen.value,
      }) || 0,
    )
  if (tripStats.value?.avoidedCO2) return Math.round(tripStats.value.avoidedCO2)
  return 0
})

const chargingTimeToUse = computed<'DC' | 'AC' | 'H' | 'AC DC split'>(() => {
  if (vehicle.value?.fuelType === 'Hydrogen') return 'H'
  if (acDcSplit.value && acDcSplit.value.acChargingTime && acDcSplit.value.dcChargingTime)
    return 'AC DC split'
  if (vehicle.value?.evNavPlugs?.some((p) => p.PowerType === 'DC')) return 'DC'
  if (
    vehicle.value?.evNavPlugs?.some(
      (p) =>
        p.PowerType === 'AC_1_PHASE' ||
        p.PowerType === 'AC_2_PHASE' ||
        p.PowerType === 'AC_2_PHASE_SPLIT' ||
        p.PowerType === 'AC_3_PHASE',
    )
  )
    return 'AC'
  return 'DC'
})

// tool tips
const emissionsTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'savings details emissions avoided',
  ),
)
const fuelSavingsTooltip = computed(() =>
  store.state.managedContent.find(
    (content) => content.Description === 'savings details fuel savings',
  ),
)
</script>
